import type { Logger } from "@expert/logging";

import ky from "ky";

import type { DefaultPayload } from "./types";

import { defaultHeaders, defaultTimeoutInMS } from "../types";

type ActivateDevicePayload = DefaultPayload & {
    activationFlowType?: string;
    claimId?: string;
    manualDeviceInfo?: {
        imei: string;
        iccid: string;
    };
};

interface ActivateDeviceResponse {
    result: string;
    reason?: string;
    /** AT&T's raw rejection descriptions. */
    details?: string[];
    /** Verizon only: the device was already active on the line. */
    alreadyActive?: boolean;
    /** Verizon only: `NOT_RETRYABLE` or `REJECTED` on a failed activation. */
    code?: string;
}

export interface ActivateDeviceResult {
    wasSuccessful: boolean;
    failureReason?: string;
    alreadyActive: boolean;
    failureCode?: string;
    /**
     * Set only when the backend said whether the same inputs can succeed on a retry (Verizon). Absent
     * means a surfaced reason is treated as un-retryable, which is the AT&T behavior.
     */
    isRetryable?: boolean;
}

/** Verizon's `code` on a failed activation. Only `NOT_RETRYABLE` means retrying cannot succeed. */
const VERIZON_FAILURE_CODES = new Set(["NOT_RETRYABLE", "REJECTED"]);

/**
 * Verizon's reason is a single `<errorMsg>` the backend already masked (no full line, IMEI or ICCID), and
 * it is specific to the failure, so it is shown as-is. None of AT&T's generic wrapper text applies.
 */
const pickVerizonFailureReason = (reason?: string): string | undefined => reason?.trim() || undefined;

// AT&T wraps the real activation-blocking reason in generic codes. These descriptions carry no
// actionable information for the expert, so they are dropped when surfacing the failure reason.
const GENERIC_ATT_ACTIVATION_ERRORS = new Set([
    "Request performed out of sequence with business process",
    "Data Error",
]);

/** Pick the actionable AT&T reason(s) out of the raw `details`, dropping generic wrapper text. */
const pickActivationFailureReason = (details?: string[]): string | undefined => {
    const meaningful = (details ?? [])
        .map((detail) => detail.trim())
        .filter((detail) => detail.length > 0 && !GENERIC_ATT_ACTIVATION_ERRORS.has(detail));
    return meaningful.length > 0 ? meaningful.join(" ") : undefined;
};

export const activateDevice = async (
    ssoAccessToken: string,
    payload: ActivateDevicePayload,
    verifiedCustomerTokenOrExtraAuthToken: string,
    logger: Logger,
    apiBaseUrl: string,
): Promise<ActivateDeviceResult> => {
    try {
        const url = `${apiBaseUrl}/api/device-activation/v1/activate`;
        const resp = await ky.post(url, {
            headers: {
                ...defaultHeaders,
                Authorization: `Bearer ${ssoAccessToken}`,
            },
            json: { ...payload, token: verifiedCustomerTokenOrExtraAuthToken },
            timeout: defaultTimeoutInMS,
        });

        const { result, reason, details, alreadyActive, code } = await resp.json<ActivateDeviceResponse>();
        if (result === "SUCCESS") {
            // Verizon reports a device that is already active on the line as a success.
            return { wasSuccessful: true, failureReason: undefined, alreadyActive: alreadyActive === true };
        }
        // Gated on partner too, so an AT&T response can never take the Verizon path on its `code` alone.
        if (payload.partner === "verizon" && code && VERIZON_FAILURE_CODES.has(code)) {
            return {
                wasSuccessful: false,
                failureReason: pickVerizonFailureReason(reason),
                alreadyActive: false,
                failureCode: code,
                isRetryable: code !== "NOT_RETRYABLE",
            };
        }
        // A 200 with a non-SUCCESS result is an AT&T business rejection (e.g. account lock). Surface
        // the actionable reason so the expert can act instead of blindly retrying an un-retryable error.
        return { wasSuccessful: false, failureReason: pickActivationFailureReason(details), alreadyActive: false };
    } catch (error) {
        logger.warn({ error }, `Error: Could not activate device.`);
        return { wasSuccessful: false, failureReason: undefined, alreadyActive: false };
    }
};
